import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import i18n from "../../../../i18n";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  isUnchanged,
  validateCreateQuestion,
  validateCreateTemplate,
} from "../../../../utils/questionnaire-validations";
import QuestionCreate from "../../../athlete-portal/profile/components/QuestionCreate";
import QuestionDisplay from "../../../athlete-portal/profile/components/QuestionDisplay";

const QuestionnaireRow = ({
  question,
  index,
  isEditing,
  editedQuestion,
  setEditedQuestion,
  onEdit,
  onDelete,
  t,
}) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: question._id,
    index,
  });

  return (
    <div
      ref={ref}
      className={`item-grabber-container-template${isDragging ? " dragging" : ""}`}
    >
      {isEditing ? (
        <QuestionCreate
          data={editedQuestion}
          setData={setEditedQuestion}
          isEdited={true}
          isOnboarding={true}
        />
      ) : (
        <QuestionDisplay
          body={question.body}
          title={question.title}
          type={question.type}
          id={question._id}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
      <button
        ref={handleRef}
        type="button"
        className="drag-handle-template"
        title={t("createQuestionnaire.grabAlt")}
      >
        <SvgIcon
          src="/img/new-icons/menu.svg"
          alt={t("createQuestionnaire.grabAlt")}
        />
      </button>
    </div>
  );
};

const CreateQuestionnaire = ({ workflow, locals }) => {
  const { assert, flash } = locals;
  const { t } = useTranslation(["coaching-portal", "common"]);

  const [questions, setQuestions] = useState(
    workflow?.options?.questionnaire?.questions
      ? workflow?.options?.questionnaire?.questions
      : []
  );

  const [adding, setAdding] = useState(
    !workflow?.options?.questionnaire?.questions
  );

  const [editedQuestion, setEditedQuestion] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    type: "short",
    body: {
      description: i18n.t(
        "athlete-portal:questionTypeDefaults.shortDescription"
      ),
    },
  });
  const [addNew, setAddNew] = useState(true);
  const [firstEdition, setFirstEdition] = useState(
    !!workflow?.options?.questionnaire?.questions
  );
  const [edit, setEdit] = useState(false);
  const reorderingRef = useRef(false);

  useEffect(() => {
    if (reorderingRef.current) {
      reorderingRef.current = false;
      return;
    }
    if (!firstEdition && !adding) {
      setEditedQuestion(null);
      setCurrentQuestion({ title: "", type: "", body: {} });
      setAdding(true);
    }
    if (questions.length === 0) {
      setAddNew(true);
    }
  }, [questions]);

  const handleAddNew = () => {
    setFirstEdition(false);
    setAdding(true);
  };

  const handleSaveQuestion = () => {
    const isEditedValid = edit
      ? validateCreateQuestion(editedQuestion, assert, flash)
      : true;
    const isCreateValid = adding
      ? validateCreateQuestion(currentQuestion, assert, flash)
      : true;

    if (isCreateValid && isEditedValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };

      const updatedQuestions = edit
        ? getEditedQuestions(editedQuestion, newQuestion, adding)
        : [...questions, newQuestion];

      setQuestions(updatedQuestions);
      setEdit(false);
      setAdding(false);
      setAddNew(true);
    }
  };

  const getEditedQuestions = (editedQuestion, newQuestion, isCreateValid) => {
    const editedQuestions = questions.map((question) =>
      question._id === editedQuestion._id ? editedQuestion : question
    );

    const result = isCreateValid
      ? [...editedQuestions, { ...newQuestion }]
      : editedQuestions;

    return result;
  };

  const onEdit = (id) => {
    const question = questions.find((question) => question._id === id);
    setEditedQuestion(question);
    setEdit(true);
    setFirstEdition(false);
  };

  const onDelete = (id) => {
    const newQuestions = questions.filter((question) => question._id !== id);

    setQuestions(newQuestions);

    if (!newQuestions.length) {
      setEdit(false);
      setAdding(true);
      setAddNew(false);
    }
  };

  const handleDragEnd = (event) => {
    if (event.canceled) return;

    const reordered = move(
      questions.map((question) => ({ id: question._id, question })),
      event
    ).map((item) => item.question);

    const orderUnchanged = reordered.every(
      (question, index) => question === questions[index]
    );

    if (orderUnchanged) return;

    reorderingRef.current = true;
    setQuestions(reordered);
  };

  const handleSaveTemplate = () => {
    const questionsArray = getQuestionsArray();

    if (!questionsArray) return false;

    const newQuestions = questionsArray.map((question, index) => ({
      title: question.title,
      type: question.type,
      body: { ...question.body },
      _id: `question-${index}`,
    }));

    // Too lazy to do a new validation xd
    const newTemplate = {
      title: "Onboarding Questionnaire",
      questions: [...newQuestions],
    };

    if (validateCreateTemplate(newTemplate, assert, flash)) {
      submit("/actions/getting-started/questionnaire", {
        method: "POST",
        body: {
          value: [...newQuestions],
          workflow: workflow._id,
          option: "questionnaire",
        },
      });
    }
  };

  const getQuestionsArray = () => {
    const isCurrentQuestionUnchanged = isUnchanged(currentQuestion);
    const isEditedQuestionValid =
      editedQuestion && validateCreateQuestion(editedQuestion, assert, flash);
    const isCurrentQuestionValid =
      !isCurrentQuestionUnchanged &&
      validateCreateQuestion(currentQuestion, assert, flash);

    if (isCurrentQuestionUnchanged) {
      if (isEditedQuestionValid) {
        return getEditedQuestions(editedQuestion, {}, false);
      } else {
        return [...questions];
      }
    } else {
      if (isCurrentQuestionValid) {
        if (isEditedQuestionValid) {
          return getEditedQuestions(editedQuestion, currentQuestion, true);
        } else {
          return [...questions, { ...currentQuestion }];
        }
      } else {
        return false;
      }
    }
  };

  return (
    <Modal
      title={t("createQuestionnaire.title")}
      className="add-checkin-template-modal"
    >
      <div className="body">
        <div className="questionnaire-add-template-questions">
          {!!questions.length && (
            <DragDropProvider onDragEnd={handleDragEnd}>
              {questions.map((question, index) => (
                <QuestionnaireRow
                  key={question._id}
                  question={question}
                  index={index}
                  isEditing={
                    !!(editedQuestion && question._id === editedQuestion._id)
                  }
                  editedQuestion={editedQuestion}
                  setEditedQuestion={setEditedQuestion}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  t={t}
                />
              ))}
            </DragDropProvider>
          )}

          {adding && (
            <QuestionCreate
              data={currentQuestion}
              setData={setCurrentQuestion}
              isOnboarding={true}
            />
          )}

          {addNew && (
            <div className="template-modal-buttons add-acc-new-footer-buttons">
              <button
                data-button-type="outline"
                onClick={firstEdition ? handleAddNew : handleSaveQuestion}
                type="button"
              >
                {t("createQuestionnaire.addNew")}
              </button>
              <a
                href={`/actions/refresh?modal=&templateId=`}
                className=" only-show-mobile acc-btn add-new"
              >
                {t("common:cancel")}
              </a>
            </div>
          )}
        </div>
      </div>
      <footer>
        <div></div>
        <button
          onClick={handleSaveTemplate}
          data-button-type="primary"
          type="button"
        >
          {t("common:save")}
        </button>
      </footer>
    </Modal>
  );
};

export default CreateQuestionnaire;
